import type { MarkdownPricingTacticsModel } from '@/shared/generated'

import { useMemo } from 'react'
import { pipe, prop } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import { useSettingsSchemasQuery } from '@/entities/core'

import {
	DescriptionDetail,
	DescriptionGroup,
	DescriptionList,
	DescriptionTerm,
} from '@/shared/components/ui/description-list'
import { Spinner } from '@/shared/components/ui/spinner'
import { intlFormat } from '@/shared/tools/dates'
import {
	getResolvedSchema,
	translateSchemaKeys,
} from '@/shared/tools/json-schema-utils'

export const PricingTacticMarkdownView = ({
	settings,
}: {
	settings: MarkdownPricingTacticsModel['params']
}) => {
	const { t } = useTranslation()
	const schemaQuery = useSettingsSchemasQuery({
		select: pipe([
			getResolvedSchema('part:tactics_md'),
			(schema) => translateSchemaKeys(schema, t),
		]),
	})
	const schema = schemaQuery.data
	const targetOptions = useMemo(
		() =>
			Object.fromEntries(
				prop(
					'properties.params.properties.periods.items.properties.target.oneOf',
					schema
				).map((s: any) => [s?.properties?.name?.const, s.title])
			),
		[schema]
	)
	const targetMesureOptions = useMemo(
		() =>
			Object.fromEntries(
				prop(
					'properties.params.properties.periods.items.properties.target.oneOf',
					schema
				).map((s: any) => [
					s?.properties?.name?.const,
					s?.properties?.value?.measure,
				])
			),
		[schema]
	)

	if (schemaQuery.isLoading) {
		return <Spinner className='opacity-50' />
	}

	return settings.periods.map((period) => (
		<DescriptionList key={period.start_date} className='flex gap-8'>
			<DescriptionGroup className='flex flex-col'>
				<DescriptionTerm>
					{t('app.start_date', {
						defaultValue: 'Start date',
					})}
				</DescriptionTerm>
				<DescriptionDetail className='font-semibold'>
					{period.start_date ? intlFormat(new Date(period.start_date)) : '-'}
				</DescriptionDetail>
			</DescriptionGroup>
			<DescriptionGroup className='flex flex-col'>
				<DescriptionTerm>
					{t('app.target.name', {
						defaultValue: 'Target name',
					})}
				</DescriptionTerm>
				<DescriptionDetail className='font-semibold'>
					{targetOptions[period.target.name] || '-'}
				</DescriptionDetail>
			</DescriptionGroup>
			<DescriptionGroup className='flex flex-col'>
				<DescriptionTerm>
					{t('app.target.value', {
						defaultValue: 'Target value',
					})}
				</DescriptionTerm>
				<DescriptionDetail className='font-semibold'>
					{period.target.value} {targetMesureOptions[period.target.name]}
				</DescriptionDetail>
			</DescriptionGroup>
		</DescriptionList>
	))
}
